import { supabase } from '@/integrations/supabase/client';
import type { Json, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import type { AppRole } from '@/services/roleService';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user;
}

export async function getCurrentSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function onAuthStateChange(callback: (event: any, session: any) => void) {
  return supabase.auth.onAuthStateChange(callback);
}

export async function signInWithPassword(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signUp(
  email: string,
  password: string,
  options?: { emailRedirectTo?: string },
) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: options ? { emailRedirectTo: options.emailRedirectTo } : undefined,
  });
  if (error) throw error;
  return data;
}

export async function signInWithOAuth(provider: 'google') {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: `${window.location.origin}/dashboard` },
  });
  if (error) throw error;
  return data;
}

export async function resetPasswordForEmail(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

export async function updateUserPassword(password: string) {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

export async function updateUserEmail(email: string) {
  const { error } = await supabase.auth.updateUser({ email });
  if (error) throw error;
}

export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, {
    body,
  });
  if (error) throw error;
  return data as T;
}

export function subscribeToChannel(
  channelName: string,
  config: { event: string; schema: string; table: string },
  callback: (payload: unknown) => void,
) {
  const channel = supabase
    .channel(channelName)
    .on('postgres_changes' as any, config as any, callback as any)
    .subscribe();
  return { channel, unsubscribe: () => supabase.removeChannel(channel) };
}

// ---------------------------------------------------------------------------
// RBAC
// ---------------------------------------------------------------------------

export async function getVenueOwner(venueId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('venue_profiles')
    .select('user_id')
    .eq('id', venueId)
    .single();

  if (error) throw error;
  return data?.user_id ?? null;
}

export async function getUserRoleRow(userId: string, venueId: string) {
  const { data, error } = await supabase
    .from('user_roles')
    .select('*')
    .eq('user_id', userId)
    .eq('venue_id', venueId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // no rows found
    throw error;
  }

  return data;
}

export async function getVenueUserRolesRows(venueId: string) {
  const { data, error } = await supabase
    .from('user_roles')
    .select('*')
    .eq('venue_id', venueId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function upsertUserRole(userId: string, venueId: string, role: AppRole) {
  const existing = await getUserRoleRow(userId, venueId);

  if (existing) {
    const { error } = await supabase
      .from('user_roles')
      .update({ role })
      .eq('user_id', userId)
      .eq('venue_id', venueId);

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: userId, venue_id: venueId, role });

    if (error) throw error;
  }
}

export async function deleteUserRole(userId: string, venueId: string) {
  const { error } = await supabase
    .from('user_roles')
    .delete()
    .eq('user_id', userId)
    .eq('venue_id', venueId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Venue Profiles
// ---------------------------------------------------------------------------

export async function getOwnedVenueProfile(userId: string) {
  const { data, error } = await supabase
    .from('venue_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getUserRoleVenueId(userId: string) {
  const { data, error } = await supabase
    .from('user_roles')
    .select('venue_id')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.venue_id ?? null;
}

export async function getVenueProfileById(venueId: string) {
  const { data, error } = await supabase
    .from('venue_profiles')
    .select('*')
    .eq('id', venueId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

export async function getTeamMembersRows(venueId: string) {
  const { data, error } = await supabase
    .from('team_members_safe')
    .select('*')
    .eq('venue_id', venueId)
    .neq('status', 'removed')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function updateTeamMemberStatus(
  memberId: string,
  status: 'active' | 'invited' | 'removed',
) {
  const { error } = await supabase.from('team_members').update({ status }).eq('id', memberId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// GM Reports
// ---------------------------------------------------------------------------

export async function getGmReportByEventId(eventId: string) {
  const { data, error } = await supabase
    .from('gm_reports')
    .select('*')
    .eq('event_id', eventId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function insertGmReport(reportData: TablesInsert<'gm_reports'>) {
  const { data, error } = await supabase.from('gm_reports').insert(reportData).select().single();

  if (error) throw error;
  return data;
}

export async function updateGmReport(reportId: string, reportData: TablesUpdate<'gm_reports'>) {
  const { data, error } = await supabase
    .from('gm_reports')
    .update(reportData)
    .eq('id', reportId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function listGmReportsForVenue(venueId: string) {
  const { data, error } = await supabase
    .from('gm_reports')
    .select('*')
    .eq('venue_id', venueId)
    .order('report_date', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export async function getEventFinalizationStatus(eventId: string) {
  const { data, error } = await supabase
    .from('events')
    .select('finalized_at')
    .eq('id', eventId)
    .single();

  if (error) throw error;
  return data;
}

export async function updateEventFinalization(eventId: string, finalizedAt: string | null) {
  const { error } = await supabase
    .from('events')
    .update({ finalized_at: finalizedAt })
    .eq('id', eventId);

  if (error) throw error;
}

export async function getLatestEventSnapshot(eventId: string) {
  const { data, error } = await supabase
    .from('gm_report_snapshots')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function insertEventSnapshot(snapshotData: {
  event_id: string;
  venue_id: string;
  snapshot_json: Json;
  finalized_by: string | null;
}) {
  const { data, error } = await supabase
    .from('gm_report_snapshots')
    .insert([snapshotData])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteEventSnapshot(snapshotId: string) {
  const { error } = await supabase.from('gm_report_snapshots').delete().eq('id', snapshotId);

  if (error) throw error;
}

export async function insertDesignPartnerApplication(
  application: TablesInsert<'design_partner_applications'>,
) {
  const { error } = await supabase.from('design_partner_applications').insert(application);
  if (error) throw error;
}

export async function getEventSyncInfo(eventId: string) {
  const { data, error } = await supabase
    .from('events')
    .select('last_synced_at, updated_at')
    .eq('id', eventId)
    .single();

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// POS Daily Summaries
// ---------------------------------------------------------------------------

export async function getPosDailySummaries(
  venueId: string,
  provider: string,
  startDate: string,
  endDate: string,
) {
  // Table added in migration 20260324_add_pos_tables.sql — cast needed until types regenerated
  const { data, error } = await supabase
    .from('pos_daily_summaries' as any)
    .select('*')
    .eq('venue_id', venueId)
    .eq('provider', provider)
    .gte('business_date', startDate)
    .lte('business_date', endDate)
    .order('business_date', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export async function getPosDailySummary(venueId: string, provider: string, date: string) {
  // Table added in migration 20260324_add_pos_tables.sql — cast needed until types regenerated
  const { data, error } = await supabase
    .from('pos_daily_summaries' as any)
    .select('*')
    .eq('venue_id', venueId)
    .eq('provider', provider)
    .eq('business_date', date)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Integrations (safe view)
// ---------------------------------------------------------------------------

export async function getIntegrationSafe(venueId: string, provider: string) {
  const { data, error } = await supabase
    .from('integrations_safe')
    .select('*')
    .eq('venue_id', venueId)
    .eq('provider', provider)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Competitor Events (table read)
// ---------------------------------------------------------------------------

export async function getCompetitorEventsRows(
  venueId: string,
  startDate: string,
  endDate?: string,
) {
  let query = supabase
    .from('competitor_events')
    .select('*')
    .eq('venue_id', venueId)
    .gte('event_date', startDate)
    .order('event_date', { ascending: true });

  if (endDate) {
    query = query.lte('event_date', endDate);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data ?? [];
}

// ---------------------------------------------------------------------------
// Competitor Events — city-level queries
// ---------------------------------------------------------------------------

export async function getCompetitorEventsByCityRows(
  city: string,
  options: {
    startDate?: string;
    endDate?: string;
    source?: string;
    limit?: number;
  } = {},
) {
  let query = (supabase as any)
    .from('competitor_events')
    .select('*')
    .eq('scrape_city', city)
    .order('event_date', { ascending: true });

  if (options.startDate) {
    query = query.gte('event_date', options.startDate);
  }
  if (options.endDate) {
    query = query.lte('event_date', options.endDate);
  }
  if (options.source) {
    query = query.eq('source', options.source);
  }
  if (options.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function getCompetitorEventsByVenueIdRows(venueId: string) {
  const { data, error } = await supabase
    .from('competitor_events')
    .select('*')
    .eq('venue_id', venueId)
    .order('event_date', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function getScrapeFreshnessRows(city: string) {
  const { data, error } = await supabase
    .from('competitor_scrape_log' as any)
    .select('*')
    .eq('scrape_city', city)
    .order('scraped_at', { ascending: false })
    .limit(10);

  if (error) throw error;
  return data ?? [];
}
